import type { RecursiveReadonly } from "../../../../types/util.ts";
import { ServicePrefix } from "../asset.ts";
import type { GcloudResourceAccess } from "../types.ts";
import type { GcloudResourceInfo, GcloudResourceType } from "./types.ts";

export const SUPPORTED_CLOUD_ASSET_TYPES = [
  "compute.googleapis.com/Instance",
  "storage.googleapis.com/Bucket",
  "iam.googleapis.com/ServiceAccount",
  "bigquery.googleapis.com/Table",
  "bigquery.googleapis.com/Dataset",
  "secretmanager.googleapis.com/Secret",
] as const;

const SERVICE_ACCOUNT_DOMAIN = ".iam.gserviceaccount.com";
export const instanceResourceName = (
  instance: string,
  zone: string,
  projectId: string
) =>
  `${ServicePrefix.compute}/projects/${projectId}/zones/${zone}/instances/${instance}`;
export const tunnelInstanceResourceName: (args: {
  name: string;
  zoneName: string;
  project: string;
}) => string = ({ name, zoneName, project }) =>
  `${ServicePrefix.iap}/projects/${project}/iap_tunnel/zones/${zoneName}/instances/${name}`;
export const bucketResourceName = (bucket: string) =>
  `${ServicePrefix.storage}/${bucket}`;

export const objectResourceName = (bucket: string, object: string) =>
  `${ServicePrefix.storage}/${bucket}/objects/${object}`;

export const zoneResourceName = (zone: string) =>
  `${ServicePrefix.compute}/zones/${zone}`;

export const serviceAccountResourceName = (
  serviceAccount: string,
  projectId: string
) =>
  `${ServicePrefix.iam}/projects/${projectId}/serviceAccounts/${serviceAccount}`;

export const isServiceAccountEmail = (email: string) =>
  email.endsWith(SERVICE_ACCOUNT_DOMAIN);

export const tableResourceName = (
  table: string,
  dataset: string,
  projectId: string
) =>
  `${ServicePrefix.bigquery}/projects/${projectId}/datasets/${dataset}/tables/${table}`;
export const datasetResourceName = (dataset: string, projectId: string) =>
  `${ServicePrefix.bigquery}/projects/${projectId}/datasets/${dataset}`;

export const secretResourceName = (secret: string, projectId: string) =>
  `${ServicePrefix.secretmanager}/projects/${projectId}/secrets/${secret}`;

export const GcloudConditionalIamSupportedResources = [
  "bucket",
  "instance",
  "tunnelInstance",
  "serviceaccount",
] as const;
export const GcloudCloudAssetResources = [
  "bucket",
  "instance",
  "secret",
  "serviceaccount",
  "table",
  "zone",
  "dataset",
] as const;

export const GcloudComputeResources = ["instance", "zone", "region"] as const;
export const GcloudStorageResources = ["bucket", "object"] as const;
export const GcloudPolicyResources = [
  "bucket",
  "instance",
  "secret",
  "tunnelInstance",
  "serviceaccount",
  "table",
  "dataset",
] as const;
export const GcloudNonPolicyResources = ["zone", "object", "region"] as const;

export const GcloudAccessShortcutServices = ["compute", "storage"] as const;
export const GcloudServices = [
  "bigquery",
  "compute",
  "iam",
  "secretmanager",
  "storage",
] as const;

const STORAGE_OBJECT_READ_PERMISSIONS = [
  // permissions to get and list objects and multipart uploads
  "storage.objects.get",
  "storage.objects.list",
  "storage.multipartUploads.list",
  "storage.multipartUploads.listParts",
] as const;

const STORAGE_BUCKET_READ_PERMISSIONS = [
  ...STORAGE_OBJECT_READ_PERMISSIONS,
  "storage.buckets.get",
  "storage.buckets.listEffectiveTags",
  "storage.buckets.listTagBindings",
] as const;

const COMPUTE_VIEW_PERMISSIONS = [
  "compute.instances.get",
  "compute.instances.list",
  "compute.instances.getEffectiveFirewalls",
  "compute.instances.getGuestAttributes",
  "compute.instances.getScreenshot",
  "compute.instances.getSerialPortOutput",
  "compute.instances.getShieldedInstanceIdentity",
  "compute.instances.getShieldedVmIdentity",
  "compute.instances.listEffectiveTags",
  "compute.instances.listReferrers",
  "compute.instances.listTagBindings",
] as const;

const INSTANCE_ACCESS_TYPES: RecursiveReadonly<
  Record<string, GcloudResourceAccess>
> = {
  admin: {
    accessName: "roles/compute.instanceAdmin",
    accessType: "role",
    nIncludedPermissions: 168,
  },
  p0CliSsh: {
    accessName: "roles/compute.osLogin",
    accessType: "role",
    nIncludedPermissions: 5,
    hidden: true,
  },
  p0CliSudoSsh: {
    accessName: "roles/compute.osAdminLogin",
    accessType: "role",
    nIncludedPermissions: 6,
    hidden: true,
  },
  ssh: {
    // google claims compute.osLogin is sufficient for vm instance SSH
    // however, it is not. it's missing "compute.instances.setMetadata"
    // so this is a custom role for now
    accessName: "ssh",
    accessType: "permission",
    permissions: [
      "compute.disks.listEffectiveTags",
      "compute.disks.listTagBindings",
      "compute.images.listEffectiveTags",
      "compute.images.listTagBindings",
      "compute.instances.get",
      "compute.instances.listEffectiveTags",
      "compute.instances.setMetadata",
      "compute.instances.listTagBindings",
      "compute.instances.osLogin",
      "compute.projects.get",
      "compute.snapshots.listEffectiveTags",
      "compute.snapshots.listTagBindings",
    ],
  },
  view: {
    accessName: "view", // view access on instances only
    accessType: "permission",
    permissions: COMPUTE_VIEW_PERMISSIONS,
  },
  edit: {
    accessName: "edit", // edit access on instances only. all permissions in view, plus update/delete/start/stop operations
    accessType: "permission",
    permissions: [
      ...COMPUTE_VIEW_PERMISSIONS,
      "compute.instances.addAccessConfig",
      "compute.instances.addMaintenancePolicies",
      "compute.instances.addResourcePolicies",
      "compute.instances.attachDisk",
      "compute.instances.createTagBinding",
      "compute.instances.delete",
      "compute.instances.deleteAccessConfig",
      "compute.instances.deleteTagBinding",
      "compute.instances.detachDisk",
      "compute.instances.osLogin",
      "compute.instances.removeMaintenancePolicies",
      "compute.instances.removeResourcePolicies",
      "compute.instances.reset",
      "compute.instances.resume",
      "compute.instances.sendDiagnosticInterrupt",
      "compute.instances.setDeletionProtection",
      "compute.instances.setDiskAutoDelete",
      "compute.instances.setLabels",
      "compute.instances.setMachineResources",
      "compute.instances.setMachineType",
      "compute.instances.setMetadata",
      "compute.instances.setMinCpuPlatform",
      "compute.instances.setName",
      "compute.instances.setScheduling",
      "compute.instances.setServiceAccount",
      "compute.instances.setShieldedInstanceIntegrityPolicy",
      "compute.instances.setTags",
      "compute.instances.simulateMaintenanceEvent",
      "compute.instances.start",
      "compute.instances.startWithEncryptionKey",
      "compute.instances.stop",
      "compute.instances.suspend",
      "compute.instances.update",
      "compute.instances.updateAccessConfig",
      "compute.instances.updateDisplayDevice",
      "compute.instances.updateNetworkInterface",
      "compute.instances.updateSecurity",
      "compute.instances.updateShieldedInstanceConfig",
      "compute.instances.updateShieldedVmConfig",
      "compute.instances.use",
      "compute.instances.useReadOnly",
    ],
  },
};

export const RESOURCE_TYPE_TO_INFO: RecursiveReadonly<
  Record<GcloudResourceType, GcloudResourceInfo>
> = {
  region: {
    permissionPrefix: "compute",
  },
  table: {
    permissionPrefix: "bigquery",
  },
  dataset: {
    permissionPrefix: "bigquery",
  },
  serviceaccount: {
    permissionPrefix: "iam",
  },
  zone: {
    permissionPrefix: "compute",
    accessTypes: {
      ...INSTANCE_ACCESS_TYPES,
      create: {
        // create requires additional permissions on the region so it is listed separately
        // additional permissions are handled on command creation
        accessName: "roles/compute.instanceAdmin",
        accessType: "role",
        nIncludedPermissions: 168,
      },
    },
  },
  bucket: {
    permissionPrefix: "storage", // object permissions can also be valid at the bucket level
    accessTypes: {
      admin: {
        accessName: "roles/storage.admin", // storage.admin is a predefined role for admin access to buckets and objects
        accessType: "role",
        nIncludedPermissions: 22, // number of storage. permissions included in role
      },
      read: {
        accessName: "read",
        accessType: "permission",
        permissions: STORAGE_BUCKET_READ_PERMISSIONS,
      },
      write: {
        accessName: "write",
        accessType: "permission",
        permissions: [
          ...STORAGE_BUCKET_READ_PERMISSIONS,
          // permissions from read, plus create, update, delete operations on bucekts, objects, and bucket metadata
          // storage.buckets.create is excluded since this permission will be restricted to a bucket
          "storage.buckets.createTagBinding",
          "storage.buckets.delete",
          "storage.buckets.deleteTagBinding",
          "storage.buckets.update",
          "storage.objects.create",
          "storage.objects.delete",
          "storage.objects.update",
          "storage.multipartUploads.abort",
          "storage.multipartUploads.create",
        ],
      },
    },
  },
  object: {
    permissionPrefix: "storage.objects",
    accessTypes: {
      admin: {
        accessName: "storage.objectAdmin", // storage.objectAdmin is a predefined role for admin accessto objects
        accessType: "role",
        nIncludedPermissions: 11, // number of storage. permissions included in role
      },
      read: {
        accessName: "read",
        accessType: "permission",
        permissions: STORAGE_OBJECT_READ_PERMISSIONS,
      },
      write: {
        accessName: "write",
        accessType: "permission",
        permissions: [
          ...STORAGE_OBJECT_READ_PERMISSIONS,
          // permissions from read, plus create, update, delete operations on  objects
          "storage.objects.create",
          "storage.objects.delete",
          "storage.objects.update",
          "storage.multipartUploads.abort",
          "storage.multipartUploads.create",
        ],
      },
    },
  },
  instance: {
    permissionPrefix: "compute",
    accessTypes: INSTANCE_ACCESS_TYPES,
  },
  secret: {
    permissionPrefix: "secretmanager",
    accessTypes: {
      accessor: {
        accessName: "roles/secretmanager.secretAccessor",
        accessType: "role",
      },
      viewer: {
        accessName: "roles/secretmanager.viewer",
        accessType: "role",
      },
      admin: {
        accessName: "roles/secretmanager.admin",
        accessType: "role",
      },
    },
  },
  tunnelInstance: {
    permissionPrefix: "iap",
    accessTypes: {
      accessor: {
        accessName: "roles/iap.tunnelResourceAccessor", // allows `gcloud start-iap-tunnel` to an IAP resource such as a compute instance (or a destination group)
        accessType: "role",
        nIncludedPermissions: 2, // number of iap. permissions included in role
      },
    },
  },
};
